import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ensureNamespaceLoaded } from "../i18n/lazyNamespaces";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Fade,
  useTheme,
  Paper,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import useSetup from "../hooks/useSetup";
import SetupStepHelp from "./setup/SetupStepHelp";

const EtapaEmpresa = ({
  company = {},
  setCompany = () => {},
  linkDigitado = "",
  setLinkDigitado = () => {},
  segmentKey = "generic",
  segmentPresetOptions = [],
  onSegmentPresetChange = () => {},
}) => {
  const theme = useTheme();
  const { t } = useTranslation("onboarding");
  useEffect(() => {
    void ensureNamespaceLoaded("onboarding");
  }, []);

  const [stepInterno, setStepInterno] = useState(company.companyName ? 1 : 0); // 0 = input site, 1 = sugestões
  const [loadingSugestao, setLoadingSugestao] = useState(false);

  const [companyName, setCompanyName] = useState(company.companyName || "");
  const [companyAbout, setCompanyAbout] = useState(company.companyAbout || "");
  const [companyWebsite, setCompanyWebsite] = useState(company.companyWebsite || "");
  const [segmento, setSegmento] = useState(company.segmento || "");
  const [publicoAlvo, setPublicoAlvo] = useState(company.publicoAlvo || "");
  const [focoPrincipal, setFocoPrincipal] = useState(company.focoPrincipal || "");
  const [maturidadeDigital, setMaturidadeDigital] = useState(company.maturidadeDigital || "");
  const [capacidadeEstrategica, setCapacidadeEstrategica] = useState(company.capacidadeEstrategica || "");

  // Snapshot / parent state can arrive after mount; keep local fields and step in sync.
  useEffect(() => {
    const c = company || {};
    const hasProfile =
      Boolean(String(c.companyName || "").trim()) ||
      Boolean(String(c.companyAbout || "").trim()) ||
      Boolean(String(c.companyWebsite || "").trim()) ||
      Boolean(String(c.segmento || "").trim()) ||
      Boolean(String(c.publicoAlvo || "").trim()) ||
      Boolean(String(c.focoPrincipal || "").trim()) ||
      Boolean(String(c.maturidadeDigital || "").trim()) ||
      Boolean(String(c.capacidadeEstrategica || "").trim());
    if (!hasProfile) return;

    setCompanyName(String(c.companyName ?? ""));
    setCompanyAbout(String(c.companyAbout ?? ""));
    setCompanyWebsite(String(c.companyWebsite ?? ""));
    setSegmento(String(c.segmento ?? ""));
    setPublicoAlvo(String(c.publicoAlvo ?? ""));
    setFocoPrincipal(String(c.focoPrincipal ?? ""));
    setMaturidadeDigital(String(c.maturidadeDigital ?? ""));
    setCapacidadeEstrategica(String(c.capacidadeEstrategica ?? ""));
    setStepInterno((s) => (s === 0 ? 2 : s));
  }, [
    company?.companyName,
    company?.companyAbout,
    company?.companyWebsite,
    company?.segmento,
    company?.publicoAlvo,
    company?.focoPrincipal,
    company?.maturidadeDigital,
    company?.capacidadeEstrategica,
  ]);

  const { generateSuggestions } = useSetup();

  const gerarSugestoes = async () => {
    setLoadingSugestao(true);
    try {
      const data = await generateSuggestions(linkDigitado); // Chama a API para gerar as sugestões
      setCompanyName(data.companyName);
      setCompanyAbout(data.companyAbout);
      setCompanyWebsite(data.companyWebsite);
      setSegmento(data.segmento);
      setPublicoAlvo(data.publicoAlvo);
      setFocoPrincipal(data.focoPrincipal);
      setMaturidadeDigital(data.maturidadeDigital);
      setCapacidadeEstrategica(data.capacidadeEstrategica);
      setStepInterno(1); // Passa para o passo de revisão das sugestões
      setCompany(data)
    } catch (error) {
      console.error("Erro ao gerar sugestões:", error);
    } finally {
      setLoadingSugestao(false);
    }
  };

  const renderFormularioEmpresa = () => (
    <>
      <Typography variant="body1" gutterBottom>
        {t("setup.companyForm.intro")}
      </Typography>

      {Array.isArray(segmentPresetOptions) && segmentPresetOptions.length > 0 ? (
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t("setup.companyForm.segmentPresetLabel")}
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={segmentKey}
            onChange={(_, v) => v != null && onSegmentPresetChange(v)}
            aria-label={t("setup.companyForm.segmentPresetAria")}
            sx={{ flexWrap: "wrap", gap: 0.5 }}
          >
            {segmentPresetOptions.map((opt) => (
              <ToggleButton key={opt.key} value={opt.key} size="small">
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t("setup.companyForm.segmentPresetHint")}
          </Typography>
        </Box>
      ) : null}

      <Paper sx={{ p: 3, mt: 3 }} elevation={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("setup.companyForm.fields.companyName")}
              value={companyName}
              onChange={(e) => {
                const value = e.target.value;
                setCompanyName(value);
                setCompany((prev) => ({ ...prev, companyName: value }));
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("setup.companyForm.fields.website")}
              value={companyWebsite}
              onChange={(e) => {
                const value = e.target.value;
                setCompanyWebsite(value);
                setCompany((prev) => ({ ...prev, companyWebsite: value }));
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("setup.companyForm.fields.about")}
              value={companyAbout}
              onChange={(e) => {
                const value = e.target.value;
                setCompanyAbout(value);
                setCompany((prev) => ({ ...prev, companyAbout: value }));
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("setup.companyForm.fields.segment")}
              value={segmento}
              onChange={(e) => {
                const value = e.target.value;
                setSegmento(value);
                setCompany((prev) => ({ ...prev, segmento: value }));
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("setup.companyForm.fields.audience")}
              value={publicoAlvo}
              onChange={(e) => {
                const value = e.target.value;
                setPublicoAlvo(value);
                setCompany((prev) => ({ ...prev, publicoAlvo: value }));
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("setup.companyForm.fields.focus")}
              value={focoPrincipal}
              onChange={(e) => {
                const value = e.target.value;
                setFocoPrincipal(value);
                setCompany((prev) => ({ ...prev, focoPrincipal: value }));
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("setup.companyForm.fields.digitalMaturity")}
              value={maturidadeDigital}
              onChange={(e) => {
                const value = e.target.value;
                setMaturidadeDigital(value);
                setCompany((prev) => ({ ...prev, maturidadeDigital: value }));
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("setup.companyForm.fields.strategy")}
              value={capacidadeEstrategica}
              onChange={(e) => {
                const value = e.target.value;
                setCapacidadeEstrategica(value);
                setCompany((prev) => ({ ...prev, capacidadeEstrategica: value }));
              }}
            />
          </Grid>
        </Grid>

        <Box mt={3} display="flex" gap={2}>
          {stepInterno !== 0 && (
            <Button variant="text" onClick={() => setStepInterno(0)}>
              {t("setup.companyForm.backToSite")}
            </Button>
          )}
        </Box>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
        {t("setup.companyForm.footerHint")}
      </Typography>
    </>
  );

  return (
    <Fade in>
      <Box sx={{ width: "100%"}}>
        <SetupStepHelp stepKey="company" />
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: theme.palette.primary.main }}>
          {t("setup.companyForm.title")}
        </Typography>

        {stepInterno === 0 && (
          <>
            <Typography variant="body1" gutterBottom>
              {t("setup.companyForm.siteIntro")}
            </Typography>
            <TextField
              fullWidth
              label={t("setup.companyForm.siteOrNameLabel")}
              value={linkDigitado}
              onChange={(e) => setLinkDigitado(e.target.value)}
              placeholder={t("setup.companyForm.sitePlaceholder")}
              sx={{ mt: 2, mb: 3 }}
            />
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                disabled={!linkDigitado || loadingSugestao}
                onClick={gerarSugestoes}
              >
                {loadingSugestao ? <CircularProgress size={24} /> : t("setup.companyForm.generateAi")}
              </Button>
              <Button variant="text" onClick={() => setStepInterno(2)}>
                {t("setup.companyForm.manual")}
              </Button>
            </Box>
          </>
        )}

        {(stepInterno === 1 || stepInterno === 2) && renderFormularioEmpresa()}
      </Box>
    </Fade>
  );
};

export default EtapaEmpresa;